// src/pages/project/ProjectSettingsMembersPage.tsx
// Members settings: ListProjectMembers + Add / UpdateRole / Remove.

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Field } from '@/features/project/ProjectModal';
import { entityRef, roleOptions } from '@/features/project/meta';
import { projectRoleMap, getStatusMapping } from '@/features/project/logic';
import type { Entity } from '@core/types/entity';
import type { ProjectMember } from '@core/types/project';

export const ProjectSettingsMembersPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useProject();
  const { t } = useI18n();

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [newProfile, setNewProfile] = useState('');
  const [newProfileName, setNewProfileName] = useState('');
  const [newRole, setNewRole] = useState('PROJECT_ROLE_MEMBER');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<ProjectMember | null>(null);

  const projectEntity = (store.currentItem as never as { entity?: Entity } | null)?.entity;

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await store.ListProjectMembers(id);
      setMembers(res.members?.members ?? []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id && !store.currentItem) store.GetProject(id);
    reload();
  }, [id]);

  const add = async () => {
    if (!id || !projectEntity || !newProfile.trim()) return;
    setBusy(true);
    try {
      await store.AddProjectMember(id, {
        project: projectEntity,
        profile: entityRef(newProfile.trim(), 'profile', projectEntity),
        role: newRole as never,
      } as never);
      setNewProfile('');
      setNewProfileName('');
      await reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (m: ProjectMember, role: string) => {
    if (!id || !projectEntity) return;
    setBusy(true);
    try {
      await store.UpdateProjectMemberRole(id, m.profile?.id ?? '', {
        project: projectEntity,
        profile: m.profile!,
        role: role as never,
      } as never);
      await reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id || !removing) return;
    setBusy(true);
    try {
      await store.RemoveProjectMember(id, removing.profile?.id ?? '');
      setRemoving(null);
      await reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('project.section.members')}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t('project.section.membersDesc')}</p>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Add member */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <Field label={t('project.members.pickPerson')}>
                <UserPicker
                  value={newProfile}
                  onChange={(profileId, displayName) => {
                    setNewProfile(profileId);
                    setNewProfileName(displayName);
                  }}
                  placeholder={t('project.members.pickPersonPlaceholder')}
                />
              </Field>
            </div>
            <div className="w-40">
              <Field label={t('project.members.role')} htmlFor="member-role">
                <Select id="member-role" value={newRole} onChange={(e) => setNewRole(e.target.value)} aria-label={t('project.members.role')}>
                  {roleOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Button size="sm" onClick={add} disabled={busy || !newProfile.trim()}>
              {t('project.members.add')}
            </Button>
          </div>
          {newProfileName && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('project.members.aboutToAdd', { name: newProfileName })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Member list */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/50" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('project.members.empty')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {members.map((m) => {
            const role = getStatusMapping(projectRoleMap, m.role);
            return (
              <li key={m.profile?.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-foreground">{m.profile?.id}</p>
                  <Badge variant={role.variant as never} size="sm">
                    {t(role.labelKey)}
                  </Badge>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <div className="w-36">
                    <Select
                      value={m.role ?? ''}
                      onChange={(e) => changeRole(m, e.target.value)}
                      disabled={busy}
                      aria-label={t('project.members.role')}
                    >
                      {roleOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(o.labelKey)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <Button size="sm" variant="danger" onClick={() => setRemoving(m)} disabled={busy}>
                    {t('common.remove')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!removing}
        title={t('project.members.removeTitle')}
        description={t('project.members.removeDesc')}
        destructive
        isConfirming={busy}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
});

export default ProjectSettingsMembersPage;
